import { FC, use } from "react";
import { Content, isFilled } from "@prismicio/client";
import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import Image from "next/image";
import { __, getPermalink } from "@/lib/i18n/functions";
import Link from "next/link";
import { appLocales, Locales } from "@/lib/i18n";
import { getByID } from "@/lib/prismic";
import { InspirationDocument } from "@/prismicio-types";

export type FeaturedInspirationListProps = SliceComponentProps<Content.FeaturedInspirationListSlice> & {
  context?: { locale?: Locales };
};

const FeaturedInspirationList: FC<FeaturedInspirationListProps> = ({ slice, context }) => {
  const locale = context?.locale || appLocales.defaultLocale;
  const { title: heading, link_label, inspirations } = slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      aria-labelledby={__("Featured inspirations", locale)}
      className="font-cabinet-grotesk container space-y-7 px-4 py-5 md:px-7.5"
    >
      <header
        id="featured-inspirations"
        className="flex flex-col items-center justify-center gap-3 md:flex-row md:justify-between"
      >
        {heading && <h2 className="text-center text-3xl font-medium">{heading}</h2>}{" "}
        {link_label && (
          <Link
            className="text-dark-primary focus-visible:ring-dark-primary/70 rounded-sm text-base font-medium underline focus-visible:ring-[3px] focus-visible:ring-offset-3 focus-visible:outline-none md:uppercase"
            href={getPermalink(locale, "inspiration_archive")}
          >
            {link_label}
          </Link>
        )}
      </header>
      <ul className="flex flex-col gap-7 md:flex-row lg:gap-7.5">
        {inspirations &&
          inspirations.length >= 1 &&
          inspirations.map((item, index) => {
            const { title, image, inspiration } = item;
            let inspirationData = undefined;

            if (isFilled.contentRelationship(inspiration)) {
              inspirationData = use(getByID(inspiration.id)) as InspirationDocument;
            }

            return (
              <li key={index} className="relative flex-1">
                <figure className="group relative aspect-square h-full max-h-[220px] w-full flex-1 overflow-hidden rounded-md sm:max-h-[340px] md:max-h-[580px] md:rounded-lg">
                  {image && (
                    <Image
                      fill
                      className="absolute object-cover transition-all duration-300 ease-in-out group-hover:scale-105"
                      src={image.url || ""}
                      alt={image.alt || ""}
                    />
                  )}
                  <div className="absolute top-1/2 left-1/2 z-20 flex w-full max-w-[436px] -translate-x-1/2 -translate-y-1/2 transform flex-col items-center justify-center gap-4 text-white lg:gap-7">
                    {isFilled.richText(title) && (
                      <div className="text-center text-3xl leading-[100%] font-medium lg:text-[45px]">
                        <PrismicRichText
                          field={title}
                          components={{
                            em: ({ children }) => <em className="font-playfair italic">{children}</em>,
                          }}
                        />
                      </div>
                    )}
                    {inspirationData?.uid &&
                      (() => {
                        const validCategory = inspirationData.data.categories.find(
                          (cat) =>
                            isFilled.contentRelationship(cat.inspirationCategory) && "slug" in cat.inspirationCategory
                        );

                        if (!validCategory || !("slug" in validCategory.inspirationCategory)) return null;

                        const slug = validCategory.inspirationCategory.slug;

                        return (
                          <Link
                            className="focus-visible:ring-dark-primary/70 relative rounded-sm text-sm font-medium underline outline-none after:absolute after:inset-0 after:-z-10 after:block after:h-screen after:w-[1000px] after:-translate-x-1/2 after:-translate-y-1/2 after:bg-black/20 focus-visible:ring-2 focus-visible:ring-offset-3"
                            href={`${getPermalink(locale, "inspiration_archive")}/${slug}#${inspirationData.uid}`}
                          >
                            {__("View the collection", locale)}
                          </Link>
                        );
                      })()}
                  </div>
                </figure>
              </li>
            );
          })}
      </ul>
    </section>
  );
};

export default FeaturedInspirationList;
